import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SocialIcon } from "react-social-icons";
import { IoClose, IoCopyOutline } from "react-icons/io5";

const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const SHARE_APPS = [
    {
        id: "facebook",
        network: "facebook",
        getHref: (enc) => `https://www.facebook.com/sharer/sharer.php?u=${enc}`,
    },
    {
        id: "whatsapp",
        network: "whatsapp",
        getHref: (enc) => `https://wa.me/?text=${enc}`,
    },
    {
        id: "instagram",
        network: "instagram",
        getHref: null,
    },
    {
        id: "snapchat",
        network: "snapchat",
        getHref: null,
    },
    {
        id: "x",
        network: "x",
        getHref: (enc) => `https://twitter.com/intent/tweet?url=${enc}`,
    },
    {
        id: "linkedin",
        network: "linkedin",
        getHref: (enc) => `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`,
    },
];

export default function SharePopup({ isOpen, onClose, shareUrl }) {
    const [copied, setCopied] = useState(false);

    const url = shareUrl || `${process.env.REACT_APP_MAIN_URL}`;
    const encodedUrl = encodeURIComponent(url);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            setCopied(false);
        }
    };

    const handleSocialClick = (app) => {
        if (app.getHref) {
            window.open(app.getHref(encodedUrl), "_blank", "noopener,noreferrer");
        } else if (navigator.share) {
            navigator.share({ url }).catch(() => { });
        } else {
            handleCopyLink();
        }
    };

    const handleShare = async () => {
        if (isMobile() && navigator.share) {
            try {
                await navigator.share({ title: "ChaaChingg", url });
            } catch (err) {
                if (err.name !== "AbortError") handleCopyLink();
            }
        } else {
            handleCopyLink();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="fixed inset-0 z-50 hidden lg:flex items-center justify-center bg-black/45 px-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.96 }}
                        transition={{ type: "spring", stiffness: 320, damping: 28, mass: 0.8 }}
                        className="w-full max-w-[430px] rounded-2xl bg-white p-4 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[18px] font-semibold leading-none text-black">Share</p>
                                <p className="mt-2 text-[14px] text-gray-700">Invite your friends to Chaa Chingg!</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md p-1 text-[#5D5D5D] hover:bg-[#F3F3F3]"
                                aria-label="Close share popup"
                            >
                                <IoClose className="text-[22px]" />
                            </button>
                        </div>

                        {/* Link field */}
                        <div className="mt-4 rounded-xl border border-[#DFDFDF] bg-[#F3F3F3] px-4 py-2">
                            <div className="flex items-center justify-between gap-3">
                                <span className="truncate text-[15px] text-[#3E3E3E]">
                                    {copied ? "Link copied!" : url}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleCopyLink}
                                    className="rounded-md p-1 text-[#3D3D3D] hover:bg-[#E5E5E5]"
                                    aria-label="Copy link"
                                >
                                    <IoCopyOutline className="text-[19px]" />
                                </button>
                            </div>
                        </div>

                        {/* Social icons */}
                        <div className="mt-4 flex items-center justify-center gap-3">
                            {SHARE_APPS.map((app) => (
                                <button
                                    key={app.id}
                                    type="button"
                                    onClick={() => handleSocialClick(app)}
                                    className="flex h-12 w-12 items-center justify-center rounded-xl"
                                    aria-label={`Share via ${app.network}`}
                                >
                                    <SocialIcon
                                        network={app.network}
                                        url="#"
                                        style={{ height: "40px", width: "40px" }}
                                        className="pointer-events-none"
                                    />
                                </button>
                            ))}
                        </div>

                        {/* Share button */}
                        <button
                            type="button"
                            onClick={handleShare}
                            className="mt-4 h-10 w-full rounded-md bg-[#BEE6FF] hover:bg-[#A8DAFA] text-[16px] font-semibold text-black"
                        >
                            Share
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
